import React from 'react';

import { SURFACE, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * Right-edge clearance reserved on the `trailing` overlay when `end` is also passed, so
 * `trailing`'s own right-aligned content never renders under a typical `end` icon (a ~19px
 * chevron plus its own margin). `end` already paints above `trailing` via z-index regardless;
 * this only keeps `trailing`'s buttons from sitting visually underneath it.
 */
const TRAILING_END_CLEARANCE_UNITS = 4;

export type SurfaceRowProps = {
  /** A small glyph, dot or icon at the row's leading edge. */
  leading?: React.ReactNode;
  primary: React.ReactNode;
  /** A muted line beside `primary`, truncated to one line. */
  secondary?: React.ReactNode;
  /**
   * Keeps the dense inline row on wide surfaces, but gives the primary and secondary copy their
   * own lines below 900px. Useful for catalogue rows, where preserving the dataset purpose is
   * more valuable than holding a rigid title column as the available width narrows.
   */
  adaptiveCopy?: boolean;
  /** A muted, right-of-content fact (a count, a cadence, a date). */
  meta?: React.ReactNode;
  /** Revealed on hover or focus-within; stays in the tab order at all times. */
  trailing?: React.ReactNode;
  /** Rendered after `trailing`, always visible regardless of hover or focus state: a permanent
   *  affordance (e.g. a chevron), unlike `trailing`'s hover/focus-within reveal. */
  end?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  role?: string;
  'aria-label'?: string;
};

/**
 * A 56px hairline row inside a plate, modelled on CatalogueDatasetRow.tsx's dense-row pattern
 * (CatalogueDatasetRow.tsx:74-143): a hairline top border, a leading glyph, a bold primary label
 * beside a muted secondary line, an optional meta fact, a trailing action slot that reveals on
 * hover or focus-within of the row while remaining reachable by keyboard at all times, and an
 * always-visible `end` slot for a permanent affordance such as a chevron.
 *
 * `trailing` is an absolutely positioned overlay (matching LensTable's own `RowHoverActions`
 * pattern), not a normal flex sibling: at rest it is invisible and out of flow, so it never
 * reserves layout width from `primary`/`meta`, and it paints its own solid backdrop plus a short
 * fade so it can cleanly cover whatever meta text sits underneath it while revealed. `end` stays a
 * normal, always-visible flex item and is given a higher stacking order so it remains visible
 * (and clickable) even while `trailing` is revealed on top of the row.
 */
export default function SurfaceRow({
  'aria-label': ariaLabel,
  adaptiveCopy = false,
  end,
  href,
  leading,
  meta,
  onClick,
  primary,
  role,
  secondary,
  trailing,
}: SurfaceRowProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const clickable = Boolean(onClick || href);

  const inlineContent = (
    <>
      {leading}
      <Typography
        component="div"
        noWrap
        sx={{ flex: secondary ? '0 0 200px' : '1 1 auto', fontSize: 14, fontWeight: 650 }}
      >
        {primary}
      </Typography>
      {secondary ? (
        <Typography color="text.secondary" noWrap sx={{ flex: '1 1 auto', fontSize: 13, minWidth: 0 }}>
          {secondary}
        </Typography>
      ) : null}
      {meta ? (
        <Typography color="text.secondary" noWrap sx={{ flex: '0 0 auto', fontSize: 12 }}>
          {meta}
        </Typography>
      ) : null}
    </>
  );

  const adaptiveContent = (
    <>
      {leading}
      <Box
        sx={{
          alignItems: 'center',
          display: 'grid',
          flex: '1 1 auto',
          gap: '2px 16px',
          gridTemplateColumns: 'minmax(0, 1fr)',
          minWidth: 0,
          '@media (min-width:900px)': {
            gridTemplateColumns: secondary ? 'minmax(180px, 0.7fr) minmax(260px, 1.3fr)' : 'minmax(0, 1fr)',
          },
        }}
      >
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 650, minWidth: 0 }}>
          {primary}
        </Typography>
        {secondary ? (
          <Typography color="text.secondary" noWrap sx={{ fontSize: 13, minWidth: 0 }}>
            {secondary}
          </Typography>
        ) : null}
      </Box>
      {meta ? (
        <Typography
          color="text.secondary"
          noWrap
          sx={{ display: 'none', flex: '0 0 auto', fontSize: 12, '@media (min-width:900px)': { display: 'block' } }}
        >
          {meta}
        </Typography>
      ) : null}
    </>
  );
  const content = adaptiveCopy ? adaptiveContent : inlineContent;

  return (
    <Box
      role={role}
      sx={{
        alignItems: 'center',
        borderTop: `1px solid ${alpha(tokens.accent, SURFACE.hairlineAlphaSubtle)}`,
        display: 'flex',
        minHeight: SURFACE.rowHeight,
        position: 'relative',
        '&:hover .surface-row-trailing, &:focus-within .surface-row-trailing': { opacity: 1 },
      }}
    >
      {clickable ? (
        <ButtonBase
          aria-label={ariaLabel}
          component={href ? 'a' : 'button'}
          href={href}
          onClick={onClick}
          sx={{
            alignItems: 'center',
            borderRadius: `${SURFACE.radius}px`,
            display: 'flex',
            flex: '1 1 auto',
            gap: 1.5,
            justifyContent: 'flex-start',
            minWidth: 0,
            px: 1,
            py: 1,
            textAlign: 'left',
            '&:hover': { backgroundColor: tokens.plateHover },
            '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: -2 },
          }}
        >
          {content}
        </ButtonBase>
      ) : (
        <Box sx={{ alignItems: 'center', display: 'flex', flex: '1 1 auto', gap: 1.5, minWidth: 0, px: 1, py: 1 }}>
          {content}
        </Box>
      )}
      {trailing ? (
        <Box
          className="surface-row-trailing"
          sx={{
            alignItems: 'center',
            bgcolor: tokens.surface,
            bottom: 0,
            display: 'flex',
            gap: 0.5,
            justifyContent: 'flex-end',
            opacity: 0,
            pl: 3,
            pr: end ? TRAILING_END_CLEARANCE_UNITS : 1,
            position: 'absolute',
            right: 0,
            top: 0,
            transition: 'opacity 120ms ease',
            zIndex: 1,
            '&::before': {
              background: `linear-gradient(to right, transparent, ${tokens.surface})`,
              bottom: 0,
              content: '""',
              left: -24,
              position: 'absolute',
              top: 0,
              width: 24,
            },
          }}
        >
          {trailing}
        </Box>
      ) : null}
      {end ? <Box sx={{ display: 'flex', flexShrink: 0, position: 'relative', zIndex: 2 }}>{end}</Box> : null}
    </Box>
  );
}
